/**
 * BuyElectro cart: side panel, add to cart, quantity + / - / typed, remove, header and
 * bottom-nav badges, toasts, and a localStorage backup of the cart.
 *
 * The source of truth is always Shopify's server cart (identified by its `cart` cookie).
 * Every mutation goes through ONE serial queue and asks Shopify to re-render the cart sections
 * in the same round trip (Section Rendering API), so the panel, the /cart page and the badges
 * always show exactly what the server holds — prices and discounts are never computed here.
 *
 * Markup hooks
 *   [data-cart-open]                 opens the panel (a link keeps its href as the no-JS fallback)
 *   [data-cart-close]                closes it
 *   [data-add-to-cart]               adds data-variant-id × data-quantity (default 1), opens the panel
 *   [data-buy-now]                   adds, then goes straight to checkout
 *   [data-cart-line] [data-qty-minus] [data-qty-plus] [data-qty-input] [data-cart-remove]
 *   [data-cart-count] [data-cart-total]   badges / header total kept in sync
 *   [data-cart-section-id]           another section to re-render with the cart (the /cart page)
 *
 * Public API: window.BECart = { open, close, add, refresh, toast, whenIdle }
 *             window.BuyElectro = { toast, lockScroll(owner), unlockScroll(owner) }
 *             window.openCartSidebar / window.closeCartSidebar for older inline handlers.
 */
(function () {
	'use strict';

	if (window.BECart) return;

	var SIDEBAR_SECTION = 'cart-sidebar';
	var STORAGE_KEY = 'buyelectro:cart';
	var DEBOUNCE_MS = 350; // + / - clicks
	var TYPING_MS = 600; // a typed quantity is sent once the shopper pauses
	var RESTORE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
	var MAX_SECTIONS = 5; // Section Rendering API limit per request

	var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
	var urls = {
		add: shopRoot + 'cart/add.js',
		change: shopRoot + 'cart/change.js',
		clear: shopRoot + 'cart/clear.js',
		cart: shopRoot + 'cart.js',
		checkout: shopRoot + 'checkout',
	};

	var state = {
		queue: Promise.resolve(),
		inFlight: 0,
		pending: new Map(), // line key -> { quantity, timer } — debounced quantity changes
		typing: new Map(), // line key -> timer — a quantity being typed, not yet sent
		sent: new Map(), // line key -> the quantity last sent, until the next render
		sections: null, // newest section HTML, rendered once the queue is idle
		resync: false, // something failed: re-fetch the sections once the queue is idle
		openAfterRender: null, // an add asked for the panel; opened after its contents render
		lineErrors: new Map(), // line key -> message, shown on the next render
		lastOpener: null,
	};

	var DEFAULT_STRINGS = {
		error: 'There was an error while updating your cart. Please try again.',
		quantityError: 'You can only add [quantity] of this item to your cart.',
		soldOut: 'This item is sold out.',
		added: 'Item added to your cart',
		removed: 'Item removed from your cart',
		updated: 'Cart updated',
		adding: 'Adding…',
		viewCart: 'View cart',
		restored: 'We restored the items from your last visit.',
	};

	/* ───────────────────────── helpers ───────────────────────── */

	function noop() {}

	function all(selector, context) {
		return Array.prototype.slice.call((context || document).querySelectorAll(selector));
	}

	function escapeValue(value) {
		return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
	}

	function sidebar() {
		return document.querySelector('[data-cart-sidebar]');
	}

	function linesFor(key) {
		return all('[data-cart-line][data-key="' + escapeValue(key) + '"]');
	}

	var stringsCache = null;
	function strings() {
		if (stringsCache) return stringsCache;
		var custom = {};
		var node = document.getElementById('CartStrings');
		if (node) {
			try {
				custom = JSON.parse(node.textContent);
			} catch (error) {
				custom = {};
			}
		}
		stringsCache = Object.assign({}, DEFAULT_STRINGS, custom);
		return stringsCache;
	}

	function quantityError(quantity) {
		return strings().quantityError.replace('[quantity]', quantity);
	}

	function sectionIds() {
		var ids = [SIDEBAR_SECTION];
		all('[data-cart-section-id]').forEach(function (element) {
			var id = element.getAttribute('data-cart-section-id');
			if (id && ids.indexOf(id) === -1) ids.push(id);
		});
		return ids.slice(0, MAX_SECTIONS);
	}

	/* ───────────────────────── network ───────────────────────── */

	function post(url, payload) {
		var body = Object.assign({}, payload, {
			sections: sectionIds(),
			sections_url: window.location.pathname,
		});

		return fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify(body),
		}).then(function (response) {
			return response
				.json()
				.catch(function () {
					return null;
				})
				.then(function (data) {
					// Cart errors come back as { status: 422, message, description }.
					if (!response.ok || !data || (typeof data.status === 'number' && data.status >= 400)) {
						var error = new Error((data && (data.description || data.message)) || strings().error);
						error.status = response.status;
						throw error;
					}
					return data;
				});
		});
	}

	function fetchSections() {
		var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds().join(','));
		// No `Accept: application/json` header: on /products/* and /cart Shopify answers that with
		// the product / cart JSON and ignores ?sections= entirely.
		return fetch(url).then(function (response) {
			if (!response.ok) throw new Error(strings().error);
			return response.json();
		});
	}

	function fetchCart() {
		return fetch(urls.cart, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (response) {
			if (!response.ok) throw new Error(strings().error);
			return response.json();
		});
	}

	/* ───────────────────────── queue ───────────────────────── */

	// Tasks run strictly one after another. A task records its outcome on `state` itself
	// (sections / resync / errors) before it settles, so settle() always sees it.
	function enqueue(task) {
		state.inFlight += 1;
		setBusy(true);
		var run = state.queue.then(task);
		state.queue = run.then(noop, noop).then(function () {
			state.inFlight -= 1;
			settle();
		});
		return run;
	}

	function isBusy() {
		return state.inFlight > 0 || state.pending.size > 0 || state.typing.size > 0;
	}

	function whenIdle() {
		return new Promise(function (resolve) {
			(function check() {
				if (isBusy()) {
					window.setTimeout(check, 60);
				} else {
					resolve();
				}
			})();
		});
	}

	function settle() {
		if (isBusy()) return;

		if (state.resync) {
			state.resync = false;
			enqueue(function () {
				return fetchSections().then(
					function (sections) {
						state.sections = sections;
					},
					noop,
				);
			});
			return;
		}

		// Never overwrite a quantity the shopper is still typing; focusout re-runs settle().
		if (hasUncommittedEdit()) return;

		var sections = state.sections;
		state.sections = null;
		if (sections) applySections(sections);
		setBusy(false);

		if (state.openAfterRender) {
			var opener = state.openAfterRender;
			state.openAfterRender = null;
			open(opener);
		}
	}

	function setBusy(on) {
		var element = sidebar();
		if (element) element.toggleAttribute('data-busy', on);
		all('[data-cart-page]').forEach(function (page) {
			page.toggleAttribute('data-busy', on);
		});
		all('[data-cart-checkout]').forEach(function (button) {
			button.setAttribute('aria-disabled', on ? 'true' : 'false');
		});
	}

	// True while the focused quantity field holds a number nobody has sent yet. A value that was
	// already sent does not count, or the totals would wait for the field to lose focus.
	function hasUncommittedEdit() {
		var active = document.activeElement;
		if (!active || !active.matches || !active.matches('[data-qty-input]')) return false;
		var line = active.closest('[data-cart-line]');
		if (!line) return false;
		var value = parseInt(active.value, 10);
		if (value === serverQuantity(line) && String(active.value) === String(value)) return false;
		return state.sent.get(line.getAttribute('data-key')) !== value;
	}

	/* ───────────────────────── rendering ───────────────────────── */

	function applySections(sections) {
		Object.keys(sections).forEach(function (id) {
			var html = sections[id];
			if (typeof html !== 'string') return; // null when a section failed to render

			var doc = new DOMParser().parseFromString(html, 'text/html');

			if (id === SIDEBAR_SECTION) {
				var fresh = doc.querySelector('[data-cart-sidebar]');
				var current = sidebar();
				if (!fresh || !current) return;
				current.setAttribute('data-count', fresh.getAttribute('data-count'));
				current.setAttribute('data-total', fresh.getAttribute('data-total'));
				var heading = current.querySelector('[data-cart-heading-count]');
				if (heading) {
					heading.textContent = fresh.getAttribute('data-count');
					heading.hidden = fresh.getAttribute('data-count') === '0';
				}
				swap(current.querySelector('[data-cart-contents]'), fresh.querySelector('[data-cart-contents]'));
			} else {
				var wrapper = document.getElementById('shopify-section-' + id);
				swap(wrapper && wrapper.querySelector('[data-cart-page]'), doc.querySelector('[data-cart-page]'));
			}
		});

		state.lineErrors.clear();
		state.sent.clear();
		syncBadges();
		saveSnapshot();
	}

	// Replace a region's contents, keeping its scroll position and the shopper's focus.
	function swap(target, source) {
		if (!target || !source) return;

		var scroller = target.querySelector('[data-cart-scroll]');
		var scrollTop = scroller ? scroller.scrollTop : 0;
		var focus = describeFocus(target);

		target.innerHTML = source.innerHTML;

		var nextScroller = target.querySelector('[data-cart-scroll]');
		if (nextScroller) nextScroller.scrollTop = scrollTop;

		state.lineErrors.forEach(function (message, key) {
			all('[data-cart-line][data-key="' + escapeValue(key) + '"] [data-cart-line-error]', target).forEach(function (node) {
				node.textContent = message;
				node.hidden = false;
			});
		});

		restoreFocus(target, focus);
	}

	var FOCUS_CONTROLS = ['data-qty-minus', 'data-qty-plus', 'data-qty-input', 'data-cart-remove', 'data-cart-checkout'];

	function describeFocus(container) {
		var active = document.activeElement;
		if (!active || !container.contains(active)) return null;
		var line = active.closest('[data-cart-line]');
		var control = FOCUS_CONTROLS.filter(function (attribute) {
			return active.hasAttribute(attribute);
		})[0];
		return {
			key: line ? line.getAttribute('data-key') : null,
			index: line ? all('[data-cart-line]', container).indexOf(line) : -1,
			control: control || null,
		};
	}

	function restoreFocus(container, focus) {
		if (!focus) return;
		var target = null;

		if (focus.key) {
			var line = container.querySelector('[data-cart-line][data-key="' + escapeValue(focus.key) + '"]');
			if (line) {
				target = focus.control ? line.querySelector('[' + focus.control + ']') : null;
				// e.g. "−" just hit 1 and is now disabled: land on the quantity instead.
				if (!target || target.disabled) target = line.querySelector('[data-qty-input]');
			} else {
				// The line was removed: move to the line that took its place.
				var lines = all('[data-cart-line]', container);
				var next = lines[Math.min(focus.index, lines.length - 1)];
				target = next ? next.querySelector('[data-cart-remove]') : null;
			}
		} else if (focus.control) {
			target = container.querySelector('[' + focus.control + ']');
		}

		if (!target) {
			target = container.querySelector('[data-cart-checkout], [data-cart-close], a[href], button') || container.closest('[data-cart-panel]');
		}
		if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
	}

	function syncBadges() {
		var element = sidebar();
		if (!element) return;
		var count = element.getAttribute('data-count');
		var total = element.getAttribute('data-total');
		all('[data-cart-count]').forEach(function (badge) {
			badge.textContent = count;
		});
		all('[data-cart-total]').forEach(function (node) {
			node.textContent = total;
		});
	}

	function announce(message) {
		var node = document.querySelector('[data-cart-status]');
		if (!node) return;
		node.textContent = '';
		window.setTimeout(function () {
			node.textContent = message;
		}, 60);
	}

	/* ───────────────────────── quantity + remove ───────────────────────── */

	function serverQuantity(line) {
		return parseInt(line.getAttribute('data-quantity'), 10) || 1;
	}

	function currentQuantity(line) {
		var input = line.querySelector('[data-qty-input]');
		var value = parseInt(input ? input.value : '', 10);
		return Number.isFinite(value) ? value : serverQuantity(line);
	}

	function maxQuantity(line) {
		var max = line.getAttribute('data-max');
		return max === null ? Infinity : parseInt(max, 10);
	}

	// Takes a line KEY. An empty message clears the error, here and in state.lineErrors,
	// so a cleared error cannot come back when swap() replays the map after a re-render.
	function showLineError(key, message) {
		if (message) state.lineErrors.set(key, message);
		else state.lineErrors.delete(key);
		linesFor(key).forEach(function (line) {
			var node = line.querySelector('[data-cart-line-error]');
			if (!node) return;
			node.textContent = message || '';
			node.hidden = !message;
		});
	}

	function reflectQuantity(key, quantity, max) {
		linesFor(key).forEach(function (line) {
			var input = line.querySelector('[data-qty-input]');
			var minus = line.querySelector('[data-qty-minus]');
			var plus = line.querySelector('[data-qty-plus]');
			if (input) input.value = quantity;
			if (minus) minus.disabled = quantity <= 1;
			if (plus) plus.disabled = quantity >= max;
		});
	}

	function markLineBusy(key, busy) {
		linesFor(key).forEach(function (copy) {
			copy.classList.toggle('is-busy', busy);
		});
	}

	function setQuantity(line, requested, immediate) {
		var key = line.getAttribute('data-key');
		var max = maxQuantity(line);
		var quantity = Math.floor(requested);

		window.clearTimeout(state.typing.get(key));
		state.typing.delete(key);

		if (!Number.isFinite(quantity)) {
			// e.g. the field was cleared and left: put the real quantity back, send nothing
			reflectQuantity(key, serverQuantity(line), max);
			if (!state.pending.has(key)) markLineBusy(key, false);
			if (!isBusy()) setBusy(false);
			return;
		}
		if (quantity <= 0) {
			removeLine(line);
			return;
		}
		if (quantity > max) {
			if (max < 1) {
				reflectQuantity(key, serverQuantity(line), max);
				showLineError(key, strings().soldOut);
				if (!state.pending.has(key)) markLineBusy(key, false);
				if (!isBusy()) setBusy(false);
				return;
			}
			quantity = max;
			showLineError(key, quantityError(max));
		} else {
			showLineError(key, '');
		}

		// Optimistic: every copy of the line (panel + cart page) shows the new number at once,
		// and its money (line total + subtotal) is dimmed until Shopify has priced it.
		reflectQuantity(key, quantity, max);
		markLineBusy(key, true);

		setBusy(true);
		var pending = state.pending.get(key);
		if (pending) window.clearTimeout(pending.timer);
		state.pending.set(key, {
			quantity: quantity,
			timer: window.setTimeout(
				function () {
					commit(key);
				},
				immediate ? 0 : DEBOUNCE_MS,
			),
		});
	}

	// While a quantity is being typed, the totals beside it still belong to the old quantity:
	// dim them and send the new number as soon as the shopper pauses — no Enter needed.
	function onQuantityTyping(input) {
		var line = input.closest('[data-cart-line]');
		if (!line) return;
		var key = line.getAttribute('data-key');

		markLineBusy(key, true);
		setBusy(true);

		window.clearTimeout(state.typing.get(key));
		state.typing.set(
			key,
			window.setTimeout(function () {
				state.typing.delete(key);
				var value = parseInt(input.value, 10);
				// An empty field is still being edited; blur decides what happens to it.
				if (Number.isFinite(value) && document.contains(line)) setQuantity(line, value, true);
			}, TYPING_MS),
		);
	}

	function commit(key) {
		var pending = state.pending.get(key);
		if (!pending) return;
		window.clearTimeout(pending.timer);
		state.pending.delete(key);
		var quantity = pending.quantity;
		state.sent.set(key, quantity);

		enqueue(function () {
			return post(urls.change, { id: key, quantity: quantity }).then(
				function (cart) {
					state.sections = cart.sections;
					var item = (cart.items || []).filter(function (entry) {
						return entry.key === key;
					})[0];
					// Shopify caps the quantity at the available stock instead of failing.
					if (item && item.quantity < quantity) state.lineErrors.set(key, quantityError(item.quantity));
					announce(strings().updated);
				},
				function (error) {
					state.lineErrors.set(key, error.message);
					state.resync = true;
				},
			);
		});
	}

	function flushPending() {
		Array.from(state.pending.keys()).forEach(commit);
	}

	function removeLine(line) {
		var key = line.getAttribute('data-key');
		window.clearTimeout(state.typing.get(key));
		state.typing.delete(key);
		var pending = state.pending.get(key);
		if (pending) {
			window.clearTimeout(pending.timer);
			state.pending.delete(key);
		}
		state.lineErrors.delete(key);

		linesFor(key).forEach(function (copy) {
			copy.classList.add('is-removing');
		});

		enqueue(function () {
			return post(urls.change, { id: key, quantity: 0 }).then(
				function (cart) {
					state.sections = cart.sections;
					announce(strings().removed);
				},
				function (error) {
					linesFor(key).forEach(function (copy) {
						copy.classList.remove('is-removing');
					});
					toast(error.message, { error: true });
					state.resync = true;
				},
			);
		});
	}

	/* ───────────────────────── change a line's variant ───────────────────────── */

	// The cart API cannot move a line to another variant, so the line goes and the chosen variant
	// is added with the same quantity. Adding is what merges: if that variant is already in the
	// cart (the shopper turned the black one blue and blue was already there), Shopify puts the
	// quantities on the one line instead of leaving two.
	function changeVariant(line, variantId, quantity) {
		var key = line.getAttribute('data-key');
		state.lineErrors.delete(key);
		linesFor(key).forEach(function (copy) {
			copy.classList.add('is-removing');
		});

		return enqueue(function () {
			return post(urls.change, { id: key, quantity: 0 })
				.then(function () {
					return post(urls.add, { items: [{ id: Number(variantId), quantity: quantity }] });
				})
				.then(
					function (response) {
						state.sections = response.sections;
						announce(strings().updated || 'Cart updated');
					},
					function (error) {
						linesFor(key).forEach(function (copy) {
							copy.classList.remove('is-removing');
						});
						toast(error.message, { error: true });
						state.resync = true;
					},
				);
		});
	}

	function onVariantChange(select) {
		var line = select.closest('[data-cart-line]');
		var box = select.closest('[data-cart-variant]');
		if (!line || !box) return;

		var json = box.querySelector('[data-cart-variants]');
		var variants;
		try {
			variants = JSON.parse(json.textContent);
		} catch (error) {
			return;
		}

		var chosen = all('[data-cart-option]', box).map(function (field) {
			return field.value;
		});
		var match = variants.filter(function (variant) {
			return variant.options.every(function (value, index) {
				return value === chosen[index];
			});
		})[0];

		if (!match || !match.available) {
			showLineError(line.getAttribute('data-key'), match ? 'That option is sold out.' : 'That combination is not available.');
			restoreSelects(box, variants);
			return;
		}

		changeVariant(line, match.id, currentQuantity(line));
	}

	// Put the boxes back to the line's own variant after a combination that cannot be bought.
	function restoreSelects(box, variants) {
		var line = box.closest('[data-cart-line]');
		var key = line && line.getAttribute('data-key');
		var current = variants.filter(function (variant) {
			return key && key.indexOf(String(variant.id)) === 0;
		})[0];
		if (!current) return;
		all('[data-cart-option]', box).forEach(function (field, index) {
			field.value = current.options[index];
		});
	}

	/* ───────────────────────── empty the cart ───────────────────────── */

	function clearCart() {
		var snapshot = cartSnapshotItems();
		if (!snapshot.length) return;

		enqueue(function () {
			return post(urls.clear, {}).then(
				function (response) {
					state.sections = response.sections;
					announce('Cart emptied');
					toast('Cart emptied', {
						duration: 6000,
						action: {
							label: 'Undo',
							onClick: function () {
								add(snapshot, { openPanel: false });
							},
						},
					});
				},
				function (error) {
					toast(error.message, { error: true });
					state.resync = true;
				},
			);
		});
	}

	// The panel carries the cart as plain data for the localStorage backup; Undo re-adds exactly that.
	function cartSnapshotItems() {
		var node = document.querySelector('[data-cart-snapshot]');
		if (!node) return [];
		try {
			var data = JSON.parse(node.textContent);
			return (data.items || []).map(function (item) {
				return { id: item.id, quantity: item.quantity, properties: item.properties };
			});
		} catch (error) {
			return [];
		}
	}

	/* ───────────────────────── add to cart ───────────────────────── */

	function normaliseItems(items) {
		return (Array.isArray(items) ? items : [items]).map(function (item) {
			var entry = {
				id: Number(item.id),
				quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
			};
			if (item.properties && Object.keys(item.properties).length) entry.properties = item.properties;
			return entry;
		});
	}

	/**
	 * Add one or more variants. Resolves with Shopify's response once the server has the items;
	 * the panel opens (unless openPanel: false) as soon as its new contents have rendered.
	 */
	function add(items, options) {
		options = options || {};
		var payload = { items: normaliseItems(items) };

		return enqueue(function () {
			return post(urls.add, payload).then(function (response) {
				state.sections = response.sections;
				if (options.openPanel !== false) state.openAfterRender = options.opener || true;
				announce(strings().added);
				return response;
			});
		});
	}

	function setTriggerLoading(trigger, loading) {
		trigger.classList.toggle('is-loading', loading);
		trigger.setAttribute('aria-busy', loading ? 'true' : 'false');
		// data-unavailable: the product page switched to a sold-out variant mid-request
		if (trigger.tagName === 'BUTTON') trigger.disabled = loading || trigger.hasAttribute('data-unavailable');

		var label = trigger.querySelector('[data-add-label]');
		if (label) {
			if (loading) {
				label.setAttribute('data-label', label.textContent);
				label.textContent = strings().adding;
			} else if (label.hasAttribute('data-label')) {
				label.textContent = label.getAttribute('data-label');
				label.removeAttribute('data-label');
			}
		}

		// Product-card icons: swap the Font Awesome glyph for a spinner while the request runs.
		var icon = trigger.querySelector('i[class*="fa-"]');
		if (icon && !label) {
			if (loading) {
				icon.setAttribute('data-icon-class', icon.className);
				icon.className = 'fa-solid fa-spinner fa-spin';
			} else if (icon.hasAttribute('data-icon-class')) {
				icon.className = icon.getAttribute('data-icon-class');
				icon.removeAttribute('data-icon-class');
			}
		}

		if (!loading) trigger.dispatchEvent(new CustomEvent('cart:trigger-idle', { bubbles: true }));
	}

	function onAddTrigger(trigger, buyNow) {
		if (trigger.classList.contains('is-loading') || trigger.getAttribute('aria-disabled') === 'true') return;
		var variantId = trigger.getAttribute('data-variant-id');
		if (!variantId) return;

		setTriggerLoading(trigger, true);
		add([{ id: variantId, quantity: trigger.getAttribute('data-quantity') || 1 }], {
			opener: trigger,
			openPanel: !buyNow,
		})
			.then(function () {
				if (!buyNow) return;
				return whenIdle().then(function () {
					markCheckoutStarted();
					window.location.href = urls.checkout;
				});
			})
			.catch(function (error) {
				toast(error.message, { error: true });
				// a successful Buy Now keeps its "Adding…" state while the browser leaves for checkout
				if (buyNow) setTriggerLoading(trigger, false);
			})
			.then(function () {
				if (!buyNow) setTriggerLoading(trigger, false);
			});
	}

	/* ───────────────────────── open / close ───────────────────────── */

	function open(opener) {
		var element = sidebar();
		if (!element) {
			window.location.href = shopRoot + 'cart';
			return;
		}
		state.lastOpener = opener instanceof Element ? opener : document.activeElement;
		if (element.hasAttribute('data-open')) return;

		element.removeAttribute('inert');
		element.setAttribute('aria-hidden', 'false');
		void element.offsetWidth; // start the slide from the closed position
		element.setAttribute('data-open', '');
		lockScroll();
		document.addEventListener('keydown', onPanelKeydown);

		var panel = element.querySelector('[data-cart-panel]');
		window.requestAnimationFrame(function () {
			if (panel) panel.focus({ preventScroll: true });
		});
	}

	function close() {
		var element = sidebar();
		if (!element || !element.hasAttribute('data-open')) return;

		element.removeAttribute('data-open');
		element.setAttribute('aria-hidden', 'true');
		element.setAttribute('inert', '');
		unlockScroll();
		document.removeEventListener('keydown', onPanelKeydown);

		var opener = state.lastOpener;
		state.lastOpener = null;
		if (opener && opener !== document.body && document.contains(opener) && typeof opener.focus === 'function') {
			opener.focus({ preventScroll: true });
		}
	}

	function onPanelKeydown(event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			close();
			return;
		}
		if (event.key !== 'Tab') return;

		var panel = sidebar().querySelector('[data-cart-panel]');
		var focusables = all('a[href], button:not([disabled]), input:not([disabled]), [tabindex]', panel).filter(function (node) {
			return node.getAttribute('tabindex') !== '-1' && node.getClientRects().length > 0;
		});
		if (!focusables.length) {
			event.preventDefault();
			panel.focus();
			return;
		}
		var first = focusables[0];
		var last = focusables[focusables.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	// Shared by every overlay (the cart panel, assets/quick-view.js): each one locks under its own
	// name and the page only scrolls again once all of them have let go. Adding to the cart from
	// the quick view opens this panel on top of it; closing the panel must not unlock the page
	// while the quick view is still open.
	var scrollLocks = {};

	function lockScroll(owner) {
		scrollLocks[owner || 'cart'] = true;
		var html = document.documentElement;
		if (html.classList.contains('be-scroll-lock')) return; // the gap was measured with the scrollbar still there
		var gap = window.innerWidth - html.clientWidth;
		// Only a real desktop scrollbar; a page that overflows sideways on a phone reports
		// hundreds of pixels here, which must not become padding.
		html.style.setProperty('--be-scrollbar-gap', gap > 0 && gap < 40 ? gap + 'px' : '0px');
		html.classList.add('be-scroll-lock');
	}

	function unlockScroll(owner) {
		delete scrollLocks[owner || 'cart'];
		if (Object.keys(scrollLocks).length) return;
		document.documentElement.classList.remove('be-scroll-lock');
	}

	/* ───────────────────────── toasts ───────────────────────── */

	function toast(message, options) {
		options = options || {};
		if (!message) return;

		var region = document.querySelector('.be-toasts');
		if (!region) {
			region = document.createElement('div');
			region.className = 'be-toasts';
			region.setAttribute('aria-live', 'polite');
			document.body.appendChild(region);
		}

		var node = document.createElement('div');
		node.className = 'be-toast' + (options.error ? ' be-toast--error' : '');
		node.setAttribute('role', options.error ? 'alert' : 'status');

		var text = document.createElement('span');
		text.textContent = message;
		node.appendChild(text);

		if (options.action) {
			var action = document.createElement(options.action.href ? 'a' : 'button');
			action.className = 'be-toast__action';
			action.textContent = options.action.label;
			if (options.action.href) {
				action.href = options.action.href;
			} else {
				action.type = 'button';
			}
			if (options.action.onClick) {
				action.addEventListener('click', function (event) {
					options.action.onClick(event);
					dismiss();
				});
			}
			node.appendChild(action);
		}

		region.appendChild(node);
		while (region.children.length > 3) region.removeChild(region.firstElementChild);

		var timer = window.setTimeout(dismiss, options.duration || (options.error ? 6000 : 3500));
		function dismiss() {
			window.clearTimeout(timer);
			node.classList.add('is-leaving');
			window.setTimeout(function () {
				if (node.parentNode) node.parentNode.removeChild(node);
			}, 220);
		}
		return dismiss;
	}

	/* ───────────────────────── localStorage backup ───────────────────────── */

	var storage = {
		read: function () {
			try {
				var value = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
				return value && Array.isArray(value.items) ? value : null;
			} catch (error) {
				return null;
			}
		},
		write: function (value) {
			try {
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
			} catch (error) {
				/* private mode / quota — the server cart still works */
			}
		},
		clear: function () {
			try {
				window.localStorage.removeItem(STORAGE_KEY);
			} catch (error) {
				/* ignore */
			}
		},
	};

	function renderedSnapshot() {
		var node = document.querySelector('[data-cart-sidebar] [data-cart-snapshot]');
		if (!node) return null;
		try {
			var data = JSON.parse(node.textContent);
			data.items = (data.items || []).map(function (item) {
				var entry = { id: item.id, quantity: item.quantity };
				if (item.properties && Object.keys(item.properties).length) entry.properties = item.properties;
				return entry;
			});
			return data;
		} catch (error) {
			return null;
		}
	}

	// Mirror the server cart into localStorage. Written only when the items actually changed,
	// so two open tabs never bounce `storage` events off each other.
	function saveSnapshot() {
		var snapshot = renderedSnapshot();
		if (!snapshot) return;
		var saved = storage.read();
		if (saved && !saved.checkoutStartedAt && JSON.stringify(saved.items) === JSON.stringify(snapshot.items)) return;
		storage.write({ items: snapshot.items, savedAt: Date.now() });
	}

	function markCheckoutStarted() {
		var saved = storage.read() || { items: [], savedAt: Date.now() };
		saved.checkoutStartedAt = Date.now();
		storage.write(saved);
	}

	/**
	 * The page rendered an empty cart, but this browser saved items last time. That happens
	 * when Shopify's cart cookie is lost (cookies cleared, expired session): put them back.
	 * Not after a checkout was started — an empty cart then most likely means the order was
	 * placed, and re-adding it would be wrong.
	 */
	function restoreSavedCart() {
		var rendered = renderedSnapshot();
		if (!rendered) return;

		var saved = storage.read();
		if (rendered.item_count > 0 || !saved || !saved.items.length) {
			saveSnapshot();
			return;
		}
		if (saved.checkoutStartedAt || !saved.savedAt || Date.now() - saved.savedAt > RESTORE_MAX_AGE_MS) {
			storage.clear();
			return;
		}

		enqueue(function () {
			// Confirm against the live cart first: a stale page must never double the quantities.
			return fetchCart().then(function (cart) {
				if (cart.item_count > 0) {
					state.resync = true;
					return;
				}
				return restoreItems(saved.items);
			}, noop);
		});
	}

	function restoreItems(saved) {
		var restored = 0;
		// Shopify puts every added line at the top of the cart, so add in reverse to get the
		// shopper's original order back.
		var items = saved.slice().reverse();

		function finish() {
			if (restored > 0) {
				toast(strings().restored, {
					action: {
						label: strings().viewCart,
						onClick: function () {
							open();
						},
					},
					duration: 6000,
				});
			} else {
				storage.clear(); // nothing is available any more — stop retrying on every page
			}
		}

		return post(urls.add, { items: items })
			.then(function (response) {
				state.sections = response.sections;
				restored = items.length;
			})
			.catch(function () {
				// One sold-out line fails the whole batch: retry line by line, skipping failures.
				return items.reduce(function (chain, item) {
					return chain.then(function () {
						return post(urls.add, { items: [item] }).then(
							function (response) {
								state.sections = response.sections;
								restored += 1;
							},
							noop,
						);
					});
				}, Promise.resolve());
			})
			.then(finish);
	}

	/* ───────────────────────── events ───────────────────────── */

	document.addEventListener('click', function (event) {
		var target = event.target;
		if (!(target instanceof Element)) return;

		var opener = target.closest('[data-cart-open]');
		if (opener) {
			event.preventDefault();
			open(opener);
			return;
		}

		if (target.closest('[data-cart-close]')) {
			event.preventDefault();
			close();
			return;
		}

		var adder = target.closest('[data-add-to-cart]');
		if (adder) {
			event.preventDefault();
			onAddTrigger(adder, false);
			return;
		}

		var buyNow = target.closest('[data-buy-now]');
		if (buyNow) {
			event.preventDefault();
			onAddTrigger(buyNow, true);
			return;
		}

		if (target.closest('[data-cart-clear]')) {
			event.preventDefault();
			clearCart();
			return;
		}

		var line = target.closest('[data-cart-line]');
		if (!line) return;

		if (target.closest('[data-qty-minus]')) {
			setQuantity(line, currentQuantity(line) - 1);
		} else if (target.closest('[data-qty-plus]')) {
			setQuantity(line, currentQuantity(line) + 1);
		} else if (target.closest('[data-cart-remove]')) {
			removeLine(line);
		}
	});

	// Non-button elements that act as buttons (product-card icons, the header cart pill).
	document.addEventListener('keydown', function (event) {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		var target = event.target;
		if (!(target instanceof Element)) return;

		if (event.key === 'Enter' && target.matches('[data-qty-input]')) {
			event.preventDefault();
			target.blur(); // commits through the change event
			return;
		}

		var fake = target.closest('[role="button"]');
		if (!fake || fake.tagName === 'BUTTON' || fake.tagName === 'A' || fake !== target) return;
		if (fake.matches('[data-cart-open], [data-add-to-cart], [data-buy-now]')) {
			event.preventDefault();
			fake.click();
		}
	});

	document.addEventListener('input', function (event) {
		var input = event.target;
		if (input instanceof Element && input.matches('[data-qty-input]')) onQuantityTyping(input);
	});

	// Enter or leaving the field sends the typed quantity straight away.
	document.addEventListener('change', function (event) {
		var input = event.target;
		if (!(input instanceof Element)) return;

		if (input.matches('[data-cart-option]')) {
			onVariantChange(input);
			return;
		}

		if (!input.matches('[data-qty-input]')) return;
		var line = input.closest('[data-cart-line]');
		if (!line) return;
		var value = parseInt(input.value, 10);
		var key = line.getAttribute('data-key');
		if (state.sent.get(key) === value && !state.typing.has(key)) return; // already on its way
		setQuantity(line, value, true);
	});

	document.addEventListener('focusout', function (event) {
		if (event.target instanceof Element && event.target.matches('[data-qty-input]')) {
			window.setTimeout(settle, 0);
		}
	});

	// Checkout must see every quantity change: flush the debounce and wait for the queue.
	document.addEventListener('submit', function (event) {
		var form = event.target;
		if (!(form instanceof HTMLFormElement) || !form.querySelector('[data-cart-checkout]')) return;
		if (!isBusy()) {
			markCheckoutStarted();
			return;
		}
		event.preventDefault();
		flushPending();
		whenIdle().then(function () {
			markCheckoutStarted();
			window.location.href = urls.checkout;
		});
	});

	// Back/forward cache restores a frozen page: the cart may have changed since.
	window.addEventListener('pageshow', function (event) {
		if (event.persisted) refresh();
	});

	// Another tab changed the cart while this one was hidden.
	document.addEventListener('visibilitychange', function () {
		if (document.visibilityState !== 'visible') return;
		var saved = storage.read();
		var rendered = renderedSnapshot();
		if (saved && rendered && JSON.stringify(saved.items) !== JSON.stringify(rendered.items)) refresh();
	});

	function refresh() {
		return enqueue(function () {
			return fetchSections().then(function (sections) {
				state.sections = sections;
			}, noop);
		});
	}

	function init() {
		restoreSavedCart();
	}

	window.BECart = {
		open: open,
		close: close,
		add: add,
		refresh: refresh,
		toast: toast,
		whenIdle: whenIdle,
	};
	window.BuyElectro = window.BuyElectro || {};
	window.BuyElectro.toast = toast;
	window.BuyElectro.lockScroll = lockScroll;
	window.BuyElectro.unlockScroll = unlockScroll;
	window.openCartSidebar = function () {
		open();
	};
	window.closeCartSidebar = close;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
